import Yoga, { Align, Direction, Edge, FlexDirection, Gutter, Justify, PositionType, type Node as YogaNode } from "yoga-layout";
import { getSceneNodeChildren, type CurvaDocument } from "@/document/model/document";
import {
  flexChildLayout,
  frameAutoLayoutMode,
  frameFlexLayout,
  type FlexLayoutAlign,
  type FlexLayoutDirection,
  type FlexLayoutJustify,
  type FrameFlexLayout,
  type FrameNode,
  type SceneNode,
} from "@/document/model/nodes";
import type { Rect } from "@/lib/math/rect";
import { frameUsesPatternLayout, patternExpandedBounds } from "./patternLayout";
import { textVisualBoundsFor } from "./textAutoSize";

export interface AutoLayoutApplyOptions {
  changedNodeIds?: Iterable<string>;
  applyBounds: (node: SceneNode, bounds: Rect) => void;
}

interface FlexLayoutConstraints {
  width: number | undefined;
  height: number | undefined;
}

interface FlexLayoutResult {
  childBounds: Map<string, Rect>;
  frameBounds?: Rect;
}

export function applyAutoLayoutToDocument(document: CurvaDocument, options: AutoLayoutApplyOptions): void {
  const changedNodeIds = options.changedNodeIds ? new Set(options.changedNodeIds) : undefined;
  for (const page of document.pages) {
    applyAutoLayoutInNodes(page.children, options.applyBounds, changedNodeIds);
  }
}

function applyAutoLayoutInNodes(
  nodes: SceneNode[],
  applyBounds: AutoLayoutApplyOptions["applyBounds"],
  changedNodeIds: Set<string> | undefined,
): boolean {
  let subtreeChanged = false;
  for (const node of nodes) {
    const children = getSceneNodeChildren(node);
    const childChanged = children ? applyAutoLayoutInNodes(children, applyBounds, changedNodeIds) : false;
    const selfChanged = !changedNodeIds || changedNodeIds.has(node.id);

    if (node.type === "frame" && frameAutoLayoutMode(node) === "flex" && (!changedNodeIds || selfChanged || childChanged || node.children.some(child => changedNodeIds.has(child.id)))) {
      applyFlexLayout(node, applyBounds);
      subtreeChanged = true;
      continue;
    }

    if (node.type === "frame" && frameUsesPatternLayout(node) && (!changedNodeIds || selfChanged || childChanged || node.children.some(child => changedNodeIds.has(child.id)))) {
      applyPatternLayout(node, applyBounds);
      subtreeChanged = true;
      continue;
    }

    subtreeChanged = subtreeChanged || selfChanged || childChanged;
  }

  return subtreeChanged;
}

function applyFlexLayout(frame: FrameNode, applyBounds: AutoLayoutApplyOptions["applyBounds"]): void {
  const { childBounds, frameBounds } = calculateFlexFrameLayout(frame);
  if (frameBounds) {
    applyBounds(frame, frameBounds);
  }

  for (const child of frame.children) {
    const nextBounds = childBounds.get(child.id);
    if (!nextBounds) {
      continue;
    }
    if (!rectsEqual(child.bounds, nextBounds)) {
      applyBounds(child, nextBounds);
    }
    if (child.type === "frame" && frameAutoLayoutMode(child) === "flex") {
      applyFlexLayout(child, applyBounds);
    }
    if (child.type === "frame" && frameUsesPatternLayout(child)) {
      applyPatternLayout(child, applyBounds);
    }
  }
}

function applyPatternLayout(frame: FrameNode, applyBounds: AutoLayoutApplyOptions["applyBounds"]): void {
  for (const child of frame.children) {
    if (child.type === "frame" && frameAutoLayoutMode(child) === "flex") {
      applyFlexLayout(child, applyBounds);
    }
    if (child.type === "frame" && frameUsesPatternLayout(child)) {
      applyPatternLayout(child, applyBounds);
    }
  }

  const expanded = patternExpandedBounds(frame);
  if (!expanded) {
    return;
  }

  // Always hug the virtual pattern extents so reducing count/gap shrinks the frame.
  if (!rectsEqual(frame.bounds, expanded)) {
    applyBounds(frame, expanded);
  }
}

export function calculateFlexFrameChildBounds(frame: FrameNode): Map<string, Rect> {
  return calculateFlexFrameLayout(frame).childBounds;
}

function calculateFlexFrameLayout(frame: FrameNode): FlexLayoutResult {
  const root = Yoga.Node.create();
  const yogaChildren: Array<{ node: SceneNode; yogaNode: YogaNode }> = [];
  const boundsById = new Map<string, Rect>();
  try {
    const layout = frameFlexLayout(frame);
    const constraints = flexFrameLayoutConstraints(frame);
    configureFlexRoot(root, frame, layout, constraints);

    const widthFixed = layout.widthSizing === "fixed";
    const heightFixed = layout.heightSizing === "fixed";

    for (const child of frame.children) {
      const childYoga = Yoga.Node.create();
      const childLayout = flexChildLayout(child);
      if (childLayout.fillWidth && !widthFixed) {
        childYoga.setWidthPercent(100);
      } else {
        childYoga.setWidth(Math.max(0, child.bounds.width));
      }
      if (childLayout.fillHeight && !heightFixed) {
        childYoga.setHeightPercent(100);
      } else {
        childYoga.setHeight(Math.max(0, child.bounds.height));
      }
      childYoga.setFlexGrow(Math.max(0, childLayout.grow));
      childYoga.setFlexShrink(Math.max(0, childLayout.shrink));
      childYoga.setAlignSelf(yogaAlign(childLayout.alignSelf));

      if (childLayout.absolute) {
        childYoga.setPositionType(PositionType.Absolute);
        childYoga.setPosition(Edge.Left, child.bounds.x - frame.bounds.x);
        childYoga.setPosition(Edge.Top, child.bounds.y - frame.bounds.y);
      } else {
        childYoga.setPositionType(PositionType.Relative);
      }

      root.insertChild(childYoga, root.getChildCount());
      yogaChildren.push({ node: child, yogaNode: childYoga });
    }

    root.calculateLayout(constraints.width, constraints.height, Direction.LTR);
    for (const child of yogaChildren) {
      const computed = child.yogaNode.getComputedLayout();
      boundsById.set(child.node.id, childLayoutBounds(frame, child.node, computed));
    }

    return {
      childBounds: boundsById,
      frameBounds: hugFrameBounds(frame, boundsById),
    };
  } finally {
    root.freeRecursive();
  }
}

function flexFrameLayoutConstraints(frame: FrameNode): FlexLayoutConstraints {
  const layout = frameFlexLayout(frame);
  const isRow = layout.direction === "row" || layout.direction === "row-reverse";

  let width: number | undefined;
  let height: number | undefined;

  const hasGrowChild = frame.children.some(child => {
    const childLayout = flexChildLayout(child);
    return !childLayout.absolute && childLayout.grow > 0;
  });

  const justifyNeedsMainAxisSpace = layout.justifyContent !== "flex-start";

  // The main axis only needs a definite size when children must be distributed
  // (non-start justify) or grown to fill it. The cross axis, however, always
  // needs a definite size when it is fixed — otherwise Yoga hugs the children
  // and alignItems (center / flex-end / stretch) has no free space to work with.
  const mainAxisNeedsSize = hasGrowChild || justifyNeedsMainAxisSpace;
  const widthIsCrossAxis = !isRow;
  const heightIsCrossAxis = isRow;

  if (layout.widthSizing === "fixed" && (widthIsCrossAxis || mainAxisNeedsSize)) {
    width = Math.max(0, frame.bounds.width);
  }
  if (layout.heightSizing === "fixed" && (heightIsCrossAxis || mainAxisNeedsSize)) {
    height = Math.max(0, frame.bounds.height);
  }

  for (const child of frame.children) {
    const childLayout = flexChildLayout(child);
    if (childLayout.absolute) {
      continue;
    }
    if (layout.widthSizing === "hug" && (childLayout.fillWidth || (childLayout.grow > 0 && isRow))) {
      width ??= Math.max(0, frame.bounds.width);
    }
    if (layout.heightSizing === "hug" && (childLayout.fillHeight || (childLayout.grow > 0 && !isRow))) {
      height ??= Math.max(0, frame.bounds.height);
    }
  }

  return { width, height };
}

function configureFlexRoot(
  root: YogaNode,
  frame: FrameNode,
  layout: FrameFlexLayout,
  constraints: FlexLayoutConstraints,
): void {
  if (constraints.width !== undefined) {
    root.setWidth(constraints.width);
  } else {
    root.setWidthAuto();
  }

  if (constraints.height !== undefined) {
    root.setHeight(constraints.height);
  } else {
    root.setHeightAuto();
  }

  root.setFlexDirection(yogaFlexDirection(layout.direction));
  root.setJustifyContent(yogaJustify(layout.justifyContent));
  root.setAlignItems(yogaAlign(layout.alignItems));
  root.setPadding(Edge.Top, Math.max(0, layout.padding.top));
  root.setPadding(Edge.Right, Math.max(0, layout.padding.right));
  root.setPadding(Edge.Bottom, Math.max(0, layout.padding.bottom));
  root.setPadding(Edge.Left, Math.max(0, layout.padding.left));
  root.setGap(Gutter.All, Math.max(0, layout.gap));
}

function childLayoutBounds(
  frame: FrameNode,
  child: SceneNode,
  computed: { left: number; top: number; width: number; height: number },
): Rect {
  const frameLayout = frameFlexLayout(frame);
  const childLayout = flexChildLayout(child);
  const isRow = frameLayout.direction === "row" || frameLayout.direction === "row-reverse";
  const intrinsicWidth = Math.max(0, child.bounds.width);
  const intrinsicHeight = Math.max(0, child.bounds.height);
  const align = resolvedAlign(childLayout.alignSelf, frameLayout.alignItems);

  let width = computed.width;
  let height = computed.height;

  const widthFixed = frameLayout.widthSizing === "fixed";
  const heightFixed = frameLayout.heightSizing === "fixed";

  if (isRow) {
    width = axisOutputSize(intrinsicWidth, computed.width, childLayout, "width", true, align, widthFixed);
    height = axisOutputSize(intrinsicHeight, computed.height, childLayout, "height", false, align, heightFixed);
  } else {
    height = axisOutputSize(intrinsicHeight, computed.height, childLayout, "height", true, align, heightFixed);
    width = axisOutputSize(intrinsicWidth, computed.width, childLayout, "width", false, align, widthFixed);
  }

  const crossAxisTextFill = textCrossAxisFillBounds(frame, child, isRow);
  if (crossAxisTextFill?.width !== undefined) {
    width = crossAxisTextFill.width;
  }
  if (crossAxisTextFill?.height !== undefined) {
    height = crossAxisTextFill.height;
  }

  let x = crossAxisTextFill?.x ?? frame.bounds.x + computed.left;
  let y = crossAxisTextFill?.y ?? frame.bounds.y + computed.top;

  // When text is aligned on a fixed cross axis, center/end-align its visual ink
  // rather than its line box, so glyphs look optically aligned (matches the
  // right-panel alignment buttons). Only meaningful when not filling the axis.
  if (child.type === "text" && !crossAxisTextFill) {
    const shift = textCrossAxisVisualShift(frame, child, isRow, align);
    if (isRow) {
      y += shift;
    } else {
      x += shift;
    }
  }

  return {
    x,
    y,
    width: Math.max(0, width),
    height: Math.max(0, height),
  };
}

/**
 * Offset needed so a text child's visual ink (not its line box) is aligned on
 * the cross axis. Returns 0 unless the cross axis is fixed and a visual-bounds
 * provider is registered.
 */
function textCrossAxisVisualShift(
  frame: FrameNode,
  child: SceneNode,
  isRow: boolean,
  align: FlexLayoutAlign,
): number {
  const frameLayout = frameFlexLayout(frame);
  const crossAxisFixed = isRow ? frameLayout.heightSizing === "fixed" : frameLayout.widthSizing === "fixed";
  if (!crossAxisFixed) {
    return 0;
  }

  const visual = textVisualBoundsFor(child as Extract<SceneNode, { type: "text" }>);
  if (!visual) {
    return 0;
  }

  const box = child.bounds;
  const startInset = isRow ? visual.y - box.y : visual.x - box.x;
  const endInset = isRow
    ? box.y + box.height - (visual.y + visual.height)
    : box.x + box.width - (visual.x + visual.width);

  switch (align === "auto" ? "flex-start" : align) {
    case "center":
      return (endInset - startInset) / 2;
    case "flex-end":
      return endInset;
    case "stretch":
      return 0;
    case "flex-start":
    default:
      return -startInset;
  }
}

function textCrossAxisFillBounds(
  frame: FrameNode,
  child: SceneNode,
  isRow: boolean,
): { x?: number; y?: number; width?: number; height?: number } | undefined {
  if (child.type !== "text") {
    return undefined;
  }

  const frameLayout = frameFlexLayout(frame);
  const childLayout = flexChildLayout(child);
  if (isRow) {
    if (!childLayout.fillHeight || frameLayout.heightSizing !== "fixed") {
      return undefined;
    }
    return {
      y: frame.bounds.y + frameLayout.padding.top,
      height: Math.max(0, frame.bounds.height - frameLayout.padding.top - frameLayout.padding.bottom),
    };
  }

  if (!childLayout.fillWidth || frameLayout.widthSizing !== "fixed") {
    return undefined;
  }
  return {
    x: frame.bounds.x + frameLayout.padding.left,
    width: Math.max(0, frame.bounds.width - frameLayout.padding.left - frameLayout.padding.right),
  };
}

function axisOutputSize(
  intrinsic: number,
  computed: number,
  childLayout: ReturnType<typeof flexChildLayout>,
  axis: "width" | "height",
  isMainAxis: boolean,
  align: FlexLayoutAlign,
  axisFixed: boolean,
): number {
  const fill = axis === "width" ? childLayout.fillWidth : childLayout.fillHeight;
  const growOnAxis = isMainAxis && childLayout.grow > 0;
  const stretchCross = !isMainAxis && align === "stretch";

  if (fill || growOnAxis || stretchCross) {
    return computed;
  }

  if (axisFixed) {
    return intrinsic;
  }

  if (isMainAxis && childLayout.shrink > 0 && computed < intrinsic - 0.001) {
    return computed;
  }

  return intrinsic;
}

function hugFrameBounds(
  frame: FrameNode,
  childBounds: Map<string, Rect>,
): Rect | undefined {
  const layout = frameFlexLayout(frame);
  const hugWidth = layout.widthSizing === "hug";
  const hugHeight = layout.heightSizing === "hug";
  if (!hugWidth && !hugHeight) {
    return undefined;
  }

  let contentRight = frame.bounds.x + layout.padding.left;
  let contentBottom = frame.bounds.y + layout.padding.top;

  for (const child of frame.children) {
    if (flexChildLayout(child).absolute) {
      continue;
    }

    const bounds = childBounds.get(child.id);
    if (!bounds) {
      continue;
    }

    contentRight = Math.max(contentRight, bounds.x + bounds.width);
    contentBottom = Math.max(contentBottom, bounds.y + bounds.height);
  }

  const nextBounds = {
    x: frame.bounds.x,
    y: frame.bounds.y,
    width: hugWidth
      ? Math.max(0, contentRight - frame.bounds.x + layout.padding.right)
      : frame.bounds.width,
    height: hugHeight
      ? Math.max(0, contentBottom - frame.bounds.y + layout.padding.bottom)
      : frame.bounds.height,
  };

  return rectsEqual(nextBounds, frame.bounds) ? undefined : nextBounds;
}

function resolvedAlign(
  alignSelf: FlexLayoutAlign,
  alignItems: Exclude<FlexLayoutAlign, "auto">,
): FlexLayoutAlign {
  return alignSelf === "auto" ? alignItems : alignSelf;
}

function yogaFlexDirection(direction: FlexLayoutDirection): FlexDirection {
  switch (direction) {
    case "row-reverse":
      return FlexDirection.RowReverse;
    case "column":
      return FlexDirection.Column;
    case "column-reverse":
      return FlexDirection.ColumnReverse;
    case "row":
    default:
      return FlexDirection.Row;
  }
}

function yogaJustify(justify: FlexLayoutJustify): Justify {
  switch (justify) {
    case "center":
      return Justify.Center;
    case "flex-end":
      return Justify.FlexEnd;
    case "space-between":
      return Justify.SpaceBetween;
    case "space-around":
      return Justify.SpaceAround;
    case "space-evenly":
      return Justify.SpaceEvenly;
    case "flex-start":
    default:
      return Justify.FlexStart;
  }
}

function yogaAlign(align: FlexLayoutAlign): Align {
  switch (align) {
    case "center":
      return Align.Center;
    case "flex-end":
      return Align.FlexEnd;
    case "stretch":
      return Align.Stretch;
    case "flex-start":
      return Align.FlexStart;
    case "auto":
    default:
      return Align.Auto;
  }
}

function rectsEqual(left: Rect, right: Rect): boolean {
  return left.x === right.x && left.y === right.y && left.width === right.width && left.height === right.height;
}
